import { createPath, Paths, ProjectPathParams } from '../../../app/router/app.router';
import { useDeliveryDetails } from './hooks/use-delivery-details.hook';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { makeTimelineEvent } from '../../orders/details/logic/timeline/order-timeline.map';
import { TimelineEventProps } from '../../../ui/components/pure/timeline/timeline.types';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import {
  makeDeliveryDetailsCardProps,
  makeDeliveryDetailsHeaderProps,
  makeDeliveryTimelineCurrentAction
} from './logic/deliveries-details-props.map';
import { WorkflowDetailsHeader } from '../../../ui/components/pure/workflow-details-header/workflow-details-header.component';
import { TimelineCard } from '../../../ui/components/pure/timeline/timeline-card/timeline-card.component';
import { DeliveriesCard } from './components/deliveries-card.component';
import { DeliveryDetailsActionsObserver } from './deliveries-details-actions-observer';
import { Redirect, useLocation } from 'react-router-dom';
import { LocationState } from '../../../ui/components/pure/list-table/list-table.types';
import { useBookingFlowType } from '../../orders/list/actions/book-order/use-booking-flow-type.hook';

export const DeliveriesDetailsPage = ({ params }: { params: ProjectPathParams<Paths.delivery> }) => {
  const { delivery, timeline, state, handlers } = useDeliveryDetails(params.deliveryID);
  const bookingFlowType = useBookingFlowType(delivery.data?.project?.id || params.projectID);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation<LocationState>();

  const timelineEvents = useMemo(() => {
    const events = (timeline.data?.events || []).map((tlEvent) => makeTimelineEvent(tlEvent, bookingFlowType));
    const currentAction = makeDeliveryTimelineCurrentAction(delivery.data, handlers.setAction);
    return [currentAction, ...events].filter((e): e is TimelineEventProps => !!e);
  }, [delivery.data, handlers.setAction, timeline.data?.events, bookingFlowType]);

  if (delivery.isLoading) {
    return <LoadingIndicator fullPage />;
  }

  if (delivery.error?.statusCode === 401) {
    return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
  }

  if (delivery.error?.statusCode === 403) {
    return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
  }

  if (delivery.error?.statusCode === 404) {
    return <ErrorFullPage preset="NOT_FOUND" entity="delivery" />;
  }

  if (delivery.error || !delivery.data) {
    return <ErrorFullPage />;
  }

  // Retail deliveries are managed from their order, and actions here (e.g. adding notes) are forbidden.
  // Old links and search results can still reach this route, so send them to the order instead.
  if (delivery.data.isRetailWithOrder()) {
    return (
      <Redirect
        to={createPath(Paths.order, {
          projectID: params.shortcode || params.projectID,
          orderID: delivery.data.workflow.id
        })}
      />
    );
  }

  const headerProps = makeDeliveryDetailsHeaderProps(
    params.projectID,
    delivery.data,
    handlers.setAction,
    bookingFlowType
  );
  const cardProps = makeDeliveryDetailsCardProps(params.projectID, delivery.data);

  return (
    <Box style={{ marginTop: isLargeScreen ? '42px' : '10px' }}>
      <WorkflowDetailsHeader {...headerProps} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column'
        }}
      >
        <Box
          sx={{
            width: isLargeScreen ? '35%' : '100%',
            paddingRight: isLargeScreen ? '3rem' : '0rem',
            flexShrink: 0
          }}
        >
          <DeliveriesCard {...cardProps} />
        </Box>
        <Box style={{ flexGrow: 1 }}>
          <TimelineCard
            isError={timeline.isError}
            isLoading={timeline.isLoading}
            title="Latest events"
            events={timelineEvents}
          />
        </Box>
        <DeliveryDetailsActionsObserver
          delivery={delivery.data}
          state={state}
          handlers={handlers}
          projectID={params.projectID}
          locationState={location.state}
        />
      </Box>
    </Box>
  );
};
